/**
 * Date formatting utility
 * Formats dates according to locale with proper Japanese support
 */

/**
 * Format date string to localized format
 * @param {string} dateString - ISO date string
 * @param {string} languageCodeDate - Locale code (e.g., 'ja-JP', 'en-US')
 * @param {object} options - Formatting options
 * @param {boolean} options.includeTime - Include time in output (default: false)
 * @returns {string} Formatted date string
 */
export const formatDate = (
  dateString,
  languageCodeDate = 'en-US',
  options = {},
) => {
  const { includeTime = false, timeZone } = options

  if (!dateString) return ''

  try {
    const normalizedString = dateString.includes('T')
      ? dateString
      : dateString.replace(' ', 'T') + 'Z'

    const date = new Date(normalizedString)

    // Custom Japanese format: YYYY年MM月DD日 HH:mm or YYYY年MM月DD日
    if (languageCodeDate === 'ja-JP') {
      const formatter = new Intl.DateTimeFormat('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        ...(includeTime && {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }),
        ...(timeZone && { timeZone }),
      })

      const parts = formatter.formatToParts(date)
      const year = parts.find((p) => p.type === 'year').value
      const month = parts.find((p) => p.type === 'month').value
      const day = parts.find((p) => p.type === 'day').value

      let formattedDate = `${year}年${month}月${day}日`

      if (includeTime) {
        const hour = parts.find((p) => p.type === 'hour').value
        const minute = parts.find((p) => p.type === 'minute').value
        formattedDate += ` ${hour}:${minute}`
      }

      return formattedDate
    }

    // English or other locales
    const formatOptions = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      ...(includeTime && {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
      ...(timeZone && { timeZone }),
    }

    return date.toLocaleString(languageCodeDate, formatOptions)
  } catch (e) {
    return ''
  }
}

/**
 * Format date for offer validity display
 * @param {string} dateString - ISO date string
 * @param {string} languageCodeDate - Locale code
 * @param {function} t - Translation function (optional)
 * @returns {string} Formatted date with localized "Valid until" prefix
 */
export const formatValidUntil = (
  dateString,
  languageCodeDate = 'en-US',
  t = null,
  options = {},
) => {
  const { includeTime = false, timeZone } = options
  const formattedDate = formatDate(dateString, languageCodeDate, {
    includeTime,
    timeZone,
  })

  if (!formattedDate) return ''

  // Use translation if provided, otherwise return just the date for Japanese
  if (languageCodeDate === 'ja-JP') {
    return formattedDate
  }

  // For English, use translation if available, otherwise use default
  const validUntilText = t ? t('homePage.validUntil') : 'Valid until'
  return `${validUntilText} ${formattedDate}`
}
